<template>
  <v-card id="edit-product">
    <v-dialog
      v-if="familyGroupChangesDialog"
      v-model="familyGroupChangesDialog"
      width="400px"
      @click:outside="familyGroupChangesDialog = false"
      @keydown.esc="familyGroupChangesDialog = false"
    >
      <i-confirmation-dialog
        :action-text="'Confirm'"
        :text="'Edit is price related & will APPLY to ALL products in Family Group.<br/>To ONLY change price related parts of this product, remove this product from the Family Group and then Edit.'"
        :title="'Confirmation'"
        @cancel="familyGroupChangesDialog = false"
        @confirm="confirmFamilyGroupChanges"
      />
    </v-dialog>

    <v-dialog
      v-if="publishDateDialog"
      v-model="publishDateDialog"
      width="400px"
      @click:outside="publishDateDialog = false"
      @keydown.esc="publishDateDialog = false"
    >
      <i-product-publish-date-dialog
        :action-text="'Confirm'"
        :text="'If NO Publish Date is set product will move to Draft tab. If Publish Date set product will move to Upcoming tab.'"
        :title="'Confirmation'"
        :tenant-country-region="tenantCountryRegion"
        @cancel="publishDateDialog = false"
        @confirm="confirmPublishDateDialog"
      />
    </v-dialog>

    <v-card-title class="i-dialog-header-div">
      <v-row class="align-center">
        <v-col cols="12" sm="9">
          <span class="g-bold g-heading-l">
            {{ newProduct ? 'New product' : 'Edit product' }}
          </span>
          <v-chip
            label
            small
            class="p-chip-guide p-chip-alert ml-4 mt-n3"
            v-if="isProductInFamilyGroup"
            ><strong>IN FAMILY GROUP</strong></v-chip
          >
        </v-col>
        <v-col cols="12" sm="3">
          <i-instructions
            instructionKey="data-maintenance/products/add-dialog"
          ></i-instructions>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-subtitle class="pt-8">
      <p class="g-bold g-heading-xs g-text-color-black">
        {{ isILR ? 'Customer' : 'Retailer' }} Marketplace Description:
      </p>
      <p class="p-product-description mb-n4">{{ retailerMartketplaceDesc }}</p>
    </v-card-subtitle>

    <div class="pt-2 pb-2 px-2 i-card-content-div p-content-div">
      <v-alert
        v-if="viewModeOnly"
        border="left"
        class="mt-1 mb-0 mx-4 i-alert"
        color="warning"
        dense
        elevation="1"
        outlined
        text
        type="info"
      >
        <template v-if="viewModeArchivedProduct"
          >View Mode only - To edit & re-publish, tick checkbox on main screen &
          Move to Draft</template
        >
      </v-alert>

      <v-alert
        v-if="product.approval && product.approval.status === 'REJECTED'"
        border="left"
        class="mt-1 mb-0 mx-4 i-alert"
        color="warning"
        dense
        elevation="1"
        outlined
        text
        type="error"
      >
        <div class="font-weight-bold mb-3">
          Your product requires the following changes to be made:
        </div>
        <div>
          <pre class="i-approval-message">{{ product.approval.message }}</pre>
        </div>
      </v-alert>

      <v-card-text>
        <v-card class="mb-4 pt-4 pb-2 px-6">
          <p class="g-bold g-heading-s g-text-color-black">
            Product Type
            <v-icon
              class="i-tooltip-color pl-1 pb-1"
              small
              v-tooltip="'Cannot be edited if product is or has been Live'"
            >
              mdi-help-circle-outline
            </v-icon>
          </p>
          <v-radio-group
            class="i-radio-group mb-n4"
            v-model="product.productType"
            :disabled="product.wasOrIsLive"
          >
            <v-row>
              <v-col
                cols="12"
                :md="productTypes.length === 1 ? 12 : 6"
                v-for="type in productTypes"
                :key="type.value"
              >
                <div
                  class="item-div mb-0"
                  :class="{
                    'item-div-active': product.productType === type.value
                  }"
                >
                  <v-radio
                    :label="type.text"
                    :value="type.value"
                    :disabled="product.productType && productTypes.length === 1"
                  ></v-radio>
                </div>
              </v-col>
            </v-row>
          </v-radio-group>
        </v-card>

        <v-expansion-panels v-model="panel" accordion>
          <v-expansion-panel ref="panel-0">
            <v-expansion-panel-header>
              <p class="g-bold g-heading-s g-text-color-black">
                Product Details
              </p>

              <v-chip
                label
                small
                class="p-chip-guide p-chip-guide-70px p-chip-ready"
                v-if="validators.validProductDetails"
                >READY</v-chip
              >
              <v-chip
                label
                small
                class="p-chip-guide p-chip-guide-70px p-chip-error"
                v-else
                >REVIEW</v-chip
              >
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <i-panel-product-details
                :newProduct="newProduct"
                :product="product"
                :originalProduct="originalProduct"
                :validators="validators"
                :fieldsRulesValues="FIELDS_RULES_VALUES"
                :isFixedWeightProduct="isFixedWeightProduct"
                :isServicesTypeProduct="isServicesTypeProduct"
                :requiresSameGtin="requiresSameGtin"
                :units="filteredUnits"
                :viewModeOnly="viewModeOnly"
                @recalculate-all-region-gpp="reCalculateAllRegionGPP"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel ref="panel-1">
            <v-expansion-panel-header>
              <p class="g-bold g-heading-s g-text-color-black">
                Product Images
              </p>

              <v-chip
                label
                small
                class="p-chip-guide p-chip-guide-70px p-chip-ready"
                v-if="hasValidProductImages"
                >READY</v-chip
              >
              <v-chip
                label
                small
                class="p-chip-guide p-chip-guide-70px p-chip-error"
                v-else
                >REVIEW</v-chip
              >
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <i-panel-product-images
                :getImageByPosition="getImageByPosition"
                :newProduct="newProduct"
                :product="product"
                :viewModeOnly="viewModeOnly"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel ref="panel-2">
            <v-expansion-panel-header>
              <p class="g-bold g-heading-s g-text-color-black">
                Ranging & Pricing
              </p>

              <v-chip
                label
                small
                class="p-chip-guide p-chip-guide-70px p-chip-ready"
                v-if="hasValidRangingAndPricing"
                >READY</v-chip
              >
              <v-chip
                label
                small
                class="p-chip-guide p-chip-guide-70px p-chip-error"
                v-else
                >REVIEW</v-chip
              >
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <i-panel-product-pricing
                :calculateProductPrice="calculateProductPrice"
                :calculatingGPP="calculatingGPP"
                :calculatingGPPRegionCodes="calculatingGPPRegionCodes"
                :getRegionCartonPrice="getRegionCartonPrice"
                :getRegionUnitPrice="getRegionUnitPrice"
                :getRegionCostPerKilo="getRegionCostPerKilo"
                :hasValidTargetStoresStatePricing="
                  hasValidTargetStoresStatePricing
                "
                :hasValidTargetStoreZonesPricing="
                  hasValidTargetStoreZonesPricing
                "
                :isFixedWeightProduct="isFixedWeightProduct"
                :isLiquorTypeProduct="isLiquorTypeProduct"
                :isNationalPricing="isNationalPricing"
                :isStatePricing="isStatePricing"
                :isZonePricing="isZonePricing"
                :newProduct="newProduct"
                :metaData="metaData"
                :product="product"
                :pricingRegions="pricingRegions"
                :usTaxCodes="usTaxCodes"
                :storesTargetedStates="storesTargetedStates"
                :storesTargetedZones="storesTargetedZones"
                :scopedPricingRegions="scopedPricingRegions"
                :targetGeography="TARGET_GEOGRAPHY"
                :targetType="TARGET_TYPE"
                :targetSelectionLocked="hasTargetSelectionLockedPromotion"
                :validRegionalPrices="validRegionalPrices"
                :viewModeOnly="viewModeOnly"
                :isServicesTypeProduct="isServicesTypeProduct"
                @recalculate-all-region-gpp="reCalculateAllRegionGPP()"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel ref="panel-3">
            <v-expansion-panel-header>
              <p class="g-bold g-heading-s g-text-color-black">
                Product Information (Optional)
              </p>

              <v-chip
                label
                small
                class="p-chip-guide p-chip-guide-90px p-chip-alert"
                v-if="hasEmptyOptionalInformation"
                >OPTIONAL</v-chip
              >
              <v-chip
                label
                small
                class="p-chip-guide p-chip-guide-70px p-chip-ready"
                v-else-if="hasValidOptionalInformation"
                >READY</v-chip
              >
              <v-chip
                label
                small
                class="p-chip-guide p-chip-guide-70px p-chip-error"
                v-else
                >REVIEW</v-chip
              >
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <i-panel-product-optional-info
                :fieldsRulesValues="FIELDS_RULES_VALUES"
                :isGroceryTypeProduct="isGroceryTypeProduct"
                :isLiquorTypeProduct="isLiquorTypeProduct"
                :newProduct="newProduct"
                :isServicesTypeProduct="isServicesTypeProduct"
                :originalProduct="originalProduct"
                :product="product"
                :viewModeOnly="viewModeOnly"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel ref="panel-4" v-if="isLiquorTypeProduct">
            <v-expansion-panel-header>
              <p class="g-bold g-heading-s g-text-color-black">
                Ratings & Recognition (Optional)
              </p>
              <v-chip
                label
                small
                class="p-chip-guide p-chip-guide-90px p-chip-alert"
                v-if="hasEmptyAwardsInformation"
                >OPTIONAL</v-chip
              >
              <v-chip
                label
                small
                class="p-chip-guide p-chip-guide-70px p-chip-ready"
                v-else-if="hasValidAwardsInformation"
                >READY</v-chip
              >
              <v-chip
                label
                small
                class="p-chip-guide p-chip-guide-70px p-chip-error"
                v-else
                >REVIEW</v-chip
              >
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <i-panel-product-awards
                :awardsRating="AWARDS_RATING"
                :fieldsRulesValues="FIELDS_RULES_VALUES"
                :newProduct="newProduct"
                :product="product"
                :viewModeOnly="viewModeOnly"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-divider v-if="!viewModeOnly" class="my-6" horizontal></v-divider>

      <v-card-text v-if="!viewModeOnly">
        <p class="g-bold g-heading-s g-text-color-black">
          {{
            ['LIVE', 'LIVE_FUTURE'].includes(product.status)
              ? 'Select Publish Date (Required)'
              : 'Select Publish Date (Optional)'
          }}
        </p>
        <!-- NOTE shown only when publish date is TODAY (Melbourne time) -->
        <v-alert
          v-if="isPublishDateToday"
          border="left"
          class="mb-3 i-alert"
          dense
          text
          outlined
          type="info"
          elevation="1"
        >
          <strong>NOTE:</strong>
          All NEW &amp; Current product edits are processed same day before 3pm
          AEDT. Promotions can then be created for GO LIVE tomorrow (if
          required),
        </v-alert>

        <v-row align="center">
          <v-col cols="12">
            <v-alert
              border="left"
              class="mb-n2 i-alert"
              dense
              text
              outlined
              type="info"
              elevation="1"
            >
              {{
                ['LIVE', 'LIVE_FUTURE'].includes(product.status)
                  ? 'This is the date in which changes to this Live product will take effect'
                  : 'This is the date in which the product will be published'
              }}
            </v-alert>
          </v-col>
          <v-col cols="12">
            <v-menu
              v-model="publishDateMenu"
              :close-on-content-click="false"
              :nudge-right="15"
              :nudge-top="30"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-bind="attrs"
                  append-icon="mdi-calendar"
                  background-color="#0000000f"
                  class="mt-0"
                  dense
                  clearable
                  label="Publish Date"
                  outlined
                  readonly
                  v-model="publishDateDisplay"
                  v-on="on"
                  :rules="[
                    (v) => {
                      if (
                        ['LIVE', 'LIVE_FUTURE', 'AWAITING_APPROVAL'].includes(
                          product.status
                        )
                      ) {
                        return !!v || 'Publish Date required'
                      } else return true
                    }
                  ]"
                ></v-text-field>
              </template>
              <v-date-picker
                :min="minAllowedPublishDate"
                v-model="metaData.publishDate"
                @change="publishDateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>
    </div>

    <v-card-actions class="i-dialog-actions-div pt-6 justify-end">
      <template v-if="!viewModeOnly">
        <v-btn rounded text class="i-btn-text-black" @click="cancel"
          >Cancel</v-btn
        >
        <v-btn
          rounded
          class="i-btn-block-secondary mr-4"
          :disabled="!formValid"
          :loading="submitting"
          @click="handleSave"
          >Save</v-btn
        >
      </template>
      <template v-else>
        <v-btn rounded class="i-btn-block-secondary mr-4" @click="cancel"
          >Close</v-btn
        >
      </template>
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash'
import moment from 'moment-timezone'
import { mapActions, mapGetters, mapState } from 'vuex'
import iConfirmationDialog from '@/components/ConfirmationDialog'
import {
  MELBOURNE_TIMEZONE,
  currentMelbourneMoment,
  minAllowedPromotionStartDate
} from '@/modules/dataMaintenance/helper/PromotionPublishSchedule'
import iPanelProductDetails from '../products/PanelProductDetails'
import iPanelProductImages from '../products/PanelProductImages'
import iPanelProductPricing from '../products/PanelProductPricing'
import iPanelProductOptionalInfo from '../products/PanelProductOptionalInfo'
import iPanelProductAwards from '../products/PanelProductAwards'
import iProductPublishDateDialog from '../dialogs/ProductPublishDateDialog'
import { calculateProductPrice } from '@/services/products'
import iInstructions from '@/components/Instructions'

const AWARDS_RATING = {
  MIN_VALUE: 1,
  MAX_VALUE: 100
}

const DEFAULT_AWARDS_DATA = {
  award1: {
    ratedBy: '',
    rating: ''
  },
  award2: {
    ratedBy: '',
    rating: ''
  },
  award3: {
    ratedBy: '',
    rating: ''
  },
  award4: {
    ratedBy: '',
    rating: ''
  }
}

const PANELS_SCROLLING_DELAY = 500

const PRODUCT_TYPES = {
  GROCERY: {
    value: 'GROCERY',
    text: 'Grocery & Fresh'
  },
  LIQUOR: {
    value: 'LIQUOR',
    text: 'Liquor'
  },
  SERVICE: {
    value: 'SERVICE',
    text: 'Service(s)'
  }
}

const PRODUCT_SOLD_AS = {
  FIXED_WEIGHT: 'FIXED_WEIGHT',
  VARIABLE_WEIGHT: 'VARIABLE_WEIGHT',
  PALLET: 'PALLET'
}

const SERVICE_ALLOWED_UNITS = new Set(['EA', 'HR', 'D', 'M2'])

const TARGET_GEOGRAPHY = {
  NATIONAL: 'NATIONAL',
  STATE: 'STATE',
  ZONE: 'ZONE'
}

const TARGET_TYPE = {
  GEOGRAPHY: 'GEOGRAPHY',
  BANNER_GROUP: 'BANNER_GROUP',
  MULTI_STORE_OPERATOR: 'MULTI_STORE_OPERATOR',
  RETAILER: 'RETAILER'
}

const FIELDS_RULES_VALUES = {
  ALLOWED_GTIN_LENGTHS: [0, 8, 12, 13, 14],
  MAX_CHARACTERS_LIQUOR_SPECIFICATIONS: 50,
  MAX_CHARACTERS_TEXT_FIELD: 30,
  MAX_CHARACTERS_TEXT_AREA: 1000,
  MAX_KEYWORDS: 5,
  MAX_UNIT_SIZE: 9999.99,
  MAX_CARTON_WEIGHT: 9999.99
}

export default {
  components: {
    iInstructions,
    iConfirmationDialog,
    iPanelProductDetails,
    iPanelProductImages,
    iPanelProductPricing,
    iPanelProductOptionalInfo,
    iPanelProductAwards,
    iProductPublishDateDialog
  },

  data: () => ({
    // Main objects
    initialBluePrint: {},
    originalProduct: {},
    metaData: {},
    validators: {},
    // Dialogs data
    familyGroupChangesDialog: false,
    publishDateDialog: false,
    // Other data
    panel: 0,
    publishDateMenu: false,
    // Product related data
    PRODUCT_TYPES,
    FIELDS_RULES_VALUES,
    // Target & Pricing data
    calculatingGPP: false,
    calculatingGPPRegionCodes: [],
    TARGET_TYPE,
    TARGET_GEOGRAPHY,
    // Awards data
    AWARDS_RATING
  }),
  created() {
    this.debounceRecalcAll = _.debounce(() => {
      this.reCalculateAllRegionGPP()
    }, 200)
  },

  props: {
    submitting: Boolean,
    units: Array,
    newProduct: Boolean,
    pricingRegions: Array,
    supplierName: String,
    supplierTimezone: String,
    usTaxCodes: {
      type: Array,
      default: () => []
    },
    product: {
      type: Object,
      required: true
    }
  },

  computed: {
    ...mapState('stores', ['retailerStores']),
    ...mapGetters('tenant', [
      'isILR',
      'tenantTradingNames',
      'tenantCountryRegion'
    ]),

    // ***
    // Product related properties
    // ***

    productTypes() {
      if (!this.tenantTradingNames?.length) return Object.values(PRODUCT_TYPES)

      const allowed = new Set(this.tenantTradingNames)
      return Object.values(PRODUCT_TYPES).filter((t) => allowed.has(t.value))
    },

    isGroceryTypeProduct() {
      return this.product.productType === PRODUCT_TYPES.GROCERY.value
    },

    isServicesTypeProduct() {
      return this.product.productType === PRODUCT_TYPES.SERVICE.value
    },

    isLiquorTypeProduct() {
      return this.product.productType === PRODUCT_TYPES.LIQUOR.value
    },

    isFixedWeightProduct() {
      return (
        this.product.soldAs === PRODUCT_SOLD_AS.FIXED_WEIGHT ||
        this.product.soldAs === PRODUCT_SOLD_AS.PALLET
      )
    },

    isVariableWeightProduct() {
      return this.product.soldAs === PRODUCT_SOLD_AS.VARIABLE_WEIGHT
    },

    isProductInFamilyGroup() {
      return !!this.product?.familyGroup
    },

    isProductStatusDraft() {
      return this.product.status === 'DRAFT'
    },

    isProductStatusLiveFuture() {
      return this.product.status === 'LIVE_FUTURE'
    },

    isProductStatusAwaitingApproval() {
      return this.product.status === 'AWAITING_APPROVAL'
    },

    isRecentBulkUploadedProduct() {
      return this.isProductStatusDraft && !this.product.hasImage
    },

    hasTargetSelectionLockedPromotion() {
      const blockedStatuses = ['AWAITING_APPROVAL', 'ACTIVE', 'UPCOMING']
      return [
        ...(this.product.activeOrFuturePriceDrops || []),
        ...(this.product.activeOrFuturePromotions || []),
        ...(this.product.activeOrFutureVolumePromotions || []),
        ...(this.product.activeOrFutureBuyGetFreePromotions || [])
      ].some((promotion) => blockedStatuses.includes(promotion.status))
    },

    // ***
    // Product naming display properties
    // ***

    retailerMartketplaceDesc() {
      const unit =
        this.product.unit === 'NONE' ? '' : this.product.unit.toUpperCase()
      const qtyMeasureName = this.isFixedWeightProduct
        ? `${this.product.unitQty}${unit}`
        : `${this.product.variableWeightTargetInKg}KG`

      return this.product.brand
        ? this.product.brand?.trim() +
            ` ${this.product.shortDescription} ${qtyMeasureName}`
        : `${this.product.shortDescription} ${qtyMeasureName}`
    },

    tradeItemDescription() {
      return this.isFixedWeightProduct
        ? `${this.retailerMartketplaceDesc} X ${this.product.packageQuantity}`
        : `${this.retailerMartketplaceDesc}`
    },

    // ***
    // Product visibility properties
    // ***

    viewModeOnly() {
      // This method endeavours to encapsulate all scenarios where "view mode" is true.  Currently, only
      // archived products cannot be edited, however, previously editing an upcoming product that is already live was
      // also not allowed (this was removed on 8/9/2023 - trello 3971)
      return this.viewModeArchivedProduct
    },

    viewModeArchivedProduct() {
      return this.product.status === 'ARCHIVE'
    },

    // ***
    // Helper properties
    // ***

    minAllowedPublishDate() {
      return minAllowedPromotionStartDate(this.tenantCountryRegion)
    },

    publishDateDisplay: {
      // getter
      get: function () {
        return this.$options.filters.formatDatePicker(this.metaData.publishDate)
      },
      // setter (we only need that since we introduced the "clearable" option on the text field
      set: function (newValue) {
        this.metaData.publishDate = newValue
      }
    },

    // ***
    // Related to geography selection & pricing
    // ***

    isNationalPricing() {
      return this.metaData.targetGeography === TARGET_GEOGRAPHY.NATIONAL
    },

    isStatePricing() {
      return this.metaData.targetGeography === TARGET_GEOGRAPHY.STATE
    },

    isZonePricing() {
      return this.metaData.targetGeography === TARGET_GEOGRAPHY.ZONE
    },
    isUSCountry() {
      return this.tenantCountryRegion === 'US'
    },

    scopedPricingRegions() {
      // the 1st national zone determines the national prices
      if (this.isNationalPricing) return [this.pricingRegions[0]]
      // the 1st state's zone determines the states prices
      if (this.isStatePricing) {
        return this.pricingRegions.reduce((states, currentZone) => {
          const additionalStateZone = states.find(
            (zone) => zone.state === currentZone.state
          )
          return additionalStateZone ? states : states.concat([currentZone])
        }, [])
      }
      if (this.isZonePricing) return this.pricingRegions
      return []
    },

    // ***
    // Product saving-related properties
    // ***

    hasFamilyGroupRelatedChanges() {
      const FG_RELATED_CHANGES = ['packageQuantity', 'regionalPrices']
      const changesArray = Object.keys(this.productChanges)
      return !!changesArray.find((change) =>
        FG_RELATED_CHANGES.includes(change)
      )
    },

    hasTaxSettingsChanges() {
      return (
        this.productChanges.hasGst !== undefined ||
        this.productChanges.hasWet !== undefined ||
        this.productChanges.usTaxCategoryId !== undefined
      )
    },

    hasRangeRuleChanges() {
      const currentRangeRule = this.constructRangeRulePayload()
      const originalRangeRule = this.constructRangeRulePayload(
        this.originalProduct,
        this.getProductMetaData(this.originalProduct, false)
      )

      return !_.isEqual(currentRangeRule, originalRangeRule)
    },

    hasProductChanges() {
      const currentBluePrint = this.createBluePrint()
      return !_.isEqual(this.initialBluePrint, currentBluePrint)
    },

    hasPublishDateChanges() {
      return (
        !!this.metaData.publishDate &&
        this.metaData.initialPublishDate !== this.metaData.publishDate
      )
    },

    productChanges() {
      return Object.keys(this.product).reduce((obj, nextProp) => {
        if (nextProp === 'packageQuantity') {
          return this.hasBusinessSignificantPackageQuantityChange()
            ? Object.assign(obj, { [nextProp]: this.product[nextProp] })
            : obj
        }

        if (nextProp === 'regionalPrices') {
          return this.hasBusinessSignificantRegionalPriceChanges()
            ? Object.assign(obj, { [nextProp]: this.product[nextProp] })
            : obj
        }

        // If property has not been modified, it is not added to the payload
        return nextProp === 'imageUrl' ||
          _.isEqual(this.product[nextProp], this.originalProduct[nextProp])
          ? obj
          : Object.assign(obj, { [nextProp]: this.product[nextProp] })
      }, {})
    },

    requiresSameGtin() {
      return +this.product.packageQuantity === 1
    },

    // ***
    // Product form validation
    // ***

    formValid() {
      return (
        this.validators.validProductDetails &&
        this.hasValidProductImages &&
        this.hasValidRangingAndPricing &&
        this.hasValidOptionalInformation &&
        this.hasValidAwardsInformation &&
        this.validProductStatusRequirements &&
        !this.calculatingGPP
      )
    },

    // ***
    // Images-related properties
    // ***

    hasValidProductImages() {
      return !!this.getImageByPosition(this.product.images, 'MAIN')
    },

    imageTypesToDelete() {
      if (!this.originalProduct.images) return []

      const deletedImageTypes = []
      this.originalProduct.images.forEach((originalImage) => {
        const type = originalImage.type
        const productImage = this.product.images.find(
          (image) => image.type === type
        )
        if (!productImage) deletedImageTypes.push(type)
      })

      return deletedImageTypes
    },

    newImages() {
      const newImages = []

      this.product.images.forEach((image) => {
        const type = image.type
        const originalImage = this.originalProduct.images?.find(
          (originalImage) => originalImage.type === type
        )
        if (image.desktop !== originalImage?.desktop) newImages.push(image)
      })

      return newImages
    },

    // ***
    // Product Ranging and Pricing-related properties
    // ***

    hasValidRangingAndPricing() {
      return (
        this.hasValidTargetTypeSelection &&
        this.hasValidProductPricing &&
        this.hasValidUsTaxCodeRequirement
      )
    },

    hasValidUsTaxCodeRequirement() {
      if (!this.newProduct || !this.isUSCountry) return true
      return !!this.product.usTaxCategoryId
    },

    hasValidTargetTypeSelection() {
      return (
        this.metaData.targetType === TARGET_TYPE.GEOGRAPHY ||
        (this.metaData.targetType === TARGET_TYPE.BANNER_GROUP &&
          !!this.metaData.targetBannerGroupIds.length) ||
        (this.metaData.targetType === TARGET_TYPE.MULTI_STORE_OPERATOR &&
          !!this.metaData.targetMultiStoreOperatorIds.length) ||
        (this.metaData.targetType === TARGET_TYPE.RETAILER &&
          !!this.metaData.targetStoreIds.length)
      )
    },

    hasValidProductPricing() {
      return (
        this.hasProductPricing &&
        this.hasValidTargetPricing &&
        !this.hasInvalidRegionalPrices &&
        !this.calculatingGPP
      )
    },

    hasProductPricing() {
      const allowZero =
        this.supplierName === 'TMA Ticketing Supplies' ||
        this.supplierName === 'Instore Ticketing Supplies'
      return (
        this.product.regionalPrices.length > 0 &&
        this.product.regionalPrices.find((zone) =>
          this.isUSCountry
            ? zone.price > 0
            : allowZero
              ? zone.price >= 0 && zone.rrp >= 0
              : zone.price > 0 && zone.rrp > 0
        )
      )
    },

    hasValidTargetPricing() {
      // This validation is only applicable when retailers are selected
      if (this.metaData.targetType !== TARGET_TYPE.RETAILER) return true
      else {
        if (this.isStatePricing) return this.hasValidTargetStoresStatePricing
        else if (this.isZonePricing) return this.hasValidTargetStoreZonesPricing
        else return true // For National pricing, validation not required as price will apply to all states or zones
      }
    },

    hasInvalidRegionalPrices() {
      if (this.isUSCountry) return false
      return this.product.regionalPrices.find((zone) => {
        const zeroPricingAllowed =
          this.supplierName === 'TMA Ticketing Supplies' ||
          this.supplierName === 'Instore Ticketing Supplies'
        const missingGPP =
          zone.grossProfitPercentage === null ||
          zone.grossProfitPercentage === undefined

        if (
          zeroPricingAllowed &&
          zone.price === 0 &&
          zone.rrp === 0 &&
          missingGPP
        ) {
          return false
        }

        return (
          (zone.price > 0 && (!zone.rrp || zone.rrp === 0)) ||
          (zone.rrp > 0 && (!zone.price || zone.price === 0)) ||
          zone.grossProfitPercentage < 0 ||
          missingGPP
        )
      })
    },

    hasValidTargetStoresStatePricing() {
      // Check for store targeted states that are not part of valid state prices
      const missingStates = []
      this.storesTargetedStates.forEach((target) => {
        if (
          !this.validRegionalPrices.find(
            (state) => state.state === target.state
          )
        )
          missingStates.push(target)
      })

      return !missingStates.length
    },

    hasValidTargetStoreZonesPricing() {
      // Check for store targeted zones that are not part of valid regional prices
      const missingZones = []
      this.storesTargetedZones.forEach((target) => {
        if (
          !this.validRegionalPrices.find(
            (region) => region.pricingRegionCode === target.pricingRegionCode
          )
        )
          missingZones.push(target)
      })

      return !missingZones.length
    },

    storesTargetedStates() {
      // Remove duplicated states
      return [
        ...new Map(
          this.storesTargetedZones.map((zone) => [zone.state, zone])
        ).values()
      ]
    },

    storesTargetedZones() {
      // 1. Find information for each targetStore
      const targetStoresInfo = this.retailerStores.filter((store) =>
        this.metaData.targetStoreIds?.includes(store.id)
      )

      // 2. Get sorted list of pricingRegions with displayName and state
      const targetedRegions = targetStoresInfo
        .map((targetStore) => {
          const regionInfo = this.pricingRegions.find(
            (region) => region.code === targetStore.pricingRegionCode
          )

          return {
            pricingRegionCode: targetStore.pricingRegionCode,
            displayName: regionInfo.displayName,
            state: regionInfo.state
          }
        })
        .sort((a, b) => (a.displayName > b.displayName ? 1 : -1))

      // 3. Remove duplicated zones (pricingRegions)
      const uniquePricingRegions = [
        ...new Map(
          targetedRegions.map((region) => [region.pricingRegionCode, region])
        ).values()
      ]

      return uniquePricingRegions
    },

    validRegionalPrices() {
      const allowZero =
        this.supplierName === 'TMA Ticketing Supplies' ||
        this.supplierName === 'Instore Ticketing Supplies'
      return this.product.regionalPrices.filter((zone) => {
        if (this.isUSCountry) return zone.price > 0

        const missingGPP =
          zone.grossProfitPercentage === null ||
          zone.grossProfitPercentage === undefined

        const validZeroCase =
          allowZero && zone.price === 0 && zone.rrp === 0 && missingGPP

        const validPrice = allowZero ? zone.price >= 0 : zone.price > 0
        const validRRP = allowZero ? zone.rrp >= 0 : zone.rrp > 0

        const hasGPP =
          zone.grossProfitPercentage !== null &&
          !Number.isNaN(zone.grossProfitPercentage)

        return validZeroCase || (validPrice && validRRP && hasGPP)
      })
    },

    // ***
    // Optional info-related properties
    // ***

    hasValidOptionalInformation() {
      return this.validLength(
        this.product.supplierKeywords,
        FIELDS_RULES_VALUES.MAX_KEYWORDS
      )
    },

    hasEmptyOptionalInformation() {
      if (this.isGroceryTypeProduct) {
        return !(
          this.product.details ||
          this.product.countryOfOrigin ||
          this.product.ingredients ||
          this.product.allergens ||
          this.product.supplierKeywords.length > 0 ||
          Object.values(this.product.dietaryInformation)?.find(
            (item) => item.value === true
          )
        )
      } else if (this.isLiquorTypeProduct) {
        return !(
          this.product.details ||
          this.product.countryOfOrigin ||
          this.product.ingredients ||
          this.product.allergens ||
          this.product.supplierKeywords.length > 0 ||
          !this.emptyLiquorOptionalInfo
        )
      } else {
        return false
      }
    },

    emptyLiquorOptionalInfo() {
      return (
        this.liquorOptionalInfo.filter((property) => !!property).length === 0
      )
    },

    liquorOptionalInfo() {
      const { awards, ...liquorOptionalInfo } = this.product.liquorInformation
      return Object.values(liquorOptionalInfo)
    },

    // ***
    // Awards info-related properties
    // ***

    hasValidAwardsInformation() {
      if (!this.isLiquorTypeProduct) return true
      return this.invalidAwards.length === 0
    },

    invalidAwards() {
      return this.liquorAwardsInfo.filter((award) => {
        return (
          (award.rating && award.rating > AWARDS_RATING.MAX_VALUE) ||
          (award.rating && award.rating < AWARDS_RATING.MIN_VALUE) ||
          (award.rating && !award.ratedBy) ||
          (!award.rating && award.ratedBy)
        )
      })
    },

    hasEmptyAwardsInformation() {
      const nonEmptyAwards = this.liquorAwardsInfo.filter(
        (award) => award.rating !== '' || award.ratedBy
      )

      return nonEmptyAwards.length === 0
    },

    liquorAwardsInfo() {
      return Object.values(this.product.liquorInformation.awards)
    },

    // ***
    // Specific validations applicable to product status
    // ***

    validProductStatusRequirements() {
      if (
        this.isProductStatusLiveFuture ||
        this.isProductStatusAwaitingApproval
      ) {
        // LIVE_FUTURE or AWAITING_APPROVAL can be saved if the product has any changes OR the publish date has been changed AND there
        // has to be a publish date (which could have been removed by an automatic rejection)
        return (
          (this.hasProductChanges || this.hasPublishDateChanges) &&
          this.metaData.publishDate
        )
      } else if (this.isProductStatusDraft) {
        // DRAFT can be saved if the product has any changes OR a publish date has been entered
        return this.hasProductChanges || !!this.metaData.publishDate
      } else if (this.product.live) {
        // LIVE product that is being edited MUST have at least one product change AND a publish date has been entered
        return this.hasProductChanges && !!this.metaData.publishDate
      }
      // NEW only needs to have at least one product change
      return this.hasProductChanges
    },

    isPublishDateToday() {
      const selected = this.metaData?.publishDate
      if (!selected) return false

      const today = currentMelbourneMoment().format('YYYY-MM-DD')

      return (
        moment.tz(selected, MELBOURNE_TIMEZONE).format('YYYY-MM-DD') === today
      )
    },

    filteredUnits() {
      const all = this.units || []
      if (!this.isServicesTypeProduct) return all

      return all
        .map((u) => ({ ...u, unit: (u.unit || '').toUpperCase() }))
        .filter((u) => SERVICE_ALLOWED_UNITS.has(u.unit))
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    // ***
    // Helper functions
    // ***

    cancel() {
      this.$emit('close')
    },

    confirmFamilyGroupChanges() {
      this.familyGroupChangesDialog = false
      this.confirmSave()
    },

    confirmPublishDateDialog(date = null) {
      if (date) this.metaData.publishDate = date
      this.publishDateDialog = false
      this.confirmSave()
    },

    createBluePrint() {
      const bluePrint = {
        ...this.product,
        ...this.metaData
      }
      // we can't take the publish date into consideration when trying to find out if there were any product changes as the publish date always
      // has to be set in order to enable the save button and so there would always be "a change" as the original product does not have a publish date
      delete bluePrint.publishDate

      return bluePrint
    },

    getImageByPosition(productImages, imagePosition) {
      return (
        productImages
          ?.filter((image) => image.type === imagePosition)
          ?.map((image) => image.desktop)[0] || ''
      )
    },

    validLength(property, length) {
      if (property) return property.length <= length
      else return true
    },

    normalizeComparableValue(value) {
      if (value === '' || value === null || value === undefined) return null

      const numericValue = Number(value)
      return Number.isNaN(numericValue) ? value : numericValue
    },

    hasBusinessSignificantPackageQuantityChange(
      product = this.product,
      originalProduct = this.originalProduct
    ) {
      return (
        this.normalizeComparableValue(product?.packageQuantity) !==
        this.normalizeComparableValue(originalProduct?.packageQuantity)
      )
    },

    getComparableRegionalPrices(regionalPrices = []) {
      return (regionalPrices || [])
        .map((region) => ({
          pricingRegionCode: region?.pricingRegionCode || null,
          price: this.normalizeComparableValue(region?.price),
          rrp: this.normalizeComparableValue(region?.rrp),
          rrpExTax: this.normalizeComparableValue(region?.rrpExTax)
        }))
        .sort((left, right) => {
          const leftKey = JSON.stringify(left)
          const rightKey = JSON.stringify(right)
          return leftKey > rightKey ? 1 : -1
        })
    },

    hasBusinessSignificantRegionalPriceChanges(
      product = this.product,
      originalProduct = this.originalProduct
    ) {
      return !_.isEqual(
        this.getComparableRegionalPrices(product?.regionalPrices),
        this.getComparableRegionalPrices(originalProduct?.regionalPrices)
      )
    },

    resetNewProductDraftForType(newType) {
      if (!this.newProduct || !newType) return

      const resetProduct = _.cloneDeep(this.originalProduct)
      Object.keys(resetProduct).forEach((key) => {
        this.product[key] = resetProduct[key]
      })

      this.product.productType = newType
      this.product.soldAs = PRODUCT_SOLD_AS.FIXED_WEIGHT

      if (newType === PRODUCT_TYPES.SERVICE.value) {
        this.product.unitQty = 1
      }

      this.metaData = this.getProductMetaData(this.product, true)
      this.validators = this.getProductValidators()
      this.calculatingGPP = false
      this.calculatingGPPRegionCodes = []
    },

    // ***
    // Related to geography selection & pricing
    // ***

    reCalculateAllRegionGPP() {
      this.product.regionalPrices.forEach((regionalPrice) => {
        this.calculateProductPrice(regionalPrice)
      })
    },

    calculateProductPrice(regionalPrice) {
      const pricingRegionCode = regionalPrice.pricingRegionCode
      this.calculatingGPP = true

      let calculationGPPRegionCodesIndex =
        this.calculatingGPPRegionCodes.indexOf(pricingRegionCode)
      if (calculationGPPRegionCodesIndex === -1) {
        this.calculatingGPPRegionCodes.push(pricingRegionCode)
        calculationGPPRegionCodesIndex =
          this.calculatingGPPRegionCodes.length - 1
      }

      const cartonPriceExTax = regionalPrice.price
      if (this.isUSCountry && cartonPriceExTax) {
        const packageQuantity = Number(this.product.packageQuantity) || 0
        const variableWeightTargetInKg =
          Number(this.product.variableWeightTargetInKg) || 0
        const derivedUnitPriceExTax = this.isFixedWeightProduct
          ? packageQuantity > 0
            ? _.round(Number(cartonPriceExTax) / packageQuantity, 2)
            : 0
          : variableWeightTargetInKg > 0
            ? _.round(Number(cartonPriceExTax) / variableWeightTargetInKg, 2)
            : 0

        regionalPrice.rrp = derivedUnitPriceExTax
        regionalPrice.grossProfitPercentage = 0
        regionalPrice.unitPriceExGst = derivedUnitPriceExTax
        regionalPrice.unitPriceIncTax = derivedUnitPriceExTax
        regionalPrice.pricePerKgExGst =
          this.isVariableWeightProduct && variableWeightTargetInKg > 0
            ? derivedUnitPriceExTax
            : null
        regionalPrice.pricePerKgIncTax =
          this.isVariableWeightProduct && variableWeightTargetInKg > 0
            ? derivedUnitPriceExTax
            : null

        this.calculatingGPP = false
        this.calculatingGPPRegionCodes.splice(calculationGPPRegionCodesIndex, 1)
        return
      }

      if (cartonPriceExTax) {
        calculateProductPrice({
          cartonPriceExTax,
          srpIncTax: regionalPrice.rrp || null,
          packageQuantity: this.product.packageQuantity,
          variableWeightTargetInKg: this.product.variableWeightTargetInKg,
          hasGst: this.product.hasGst,
          hasWet: this.product.hasWet
        })
          .then((data) => {
            regionalPrice.grossProfitPercentage = data.gpp
            regionalPrice.unitPriceExGst = data.unitPriceExTax
            regionalPrice.unitPriceIncTax = data.unitPriceIncTax
            regionalPrice.pricePerKgIncTax = data.pricePerKgIncTax
            regionalPrice.pricePerKgExGst = data.pricePerKgExTax
            if (this.isServicesTypeProduct) {
              if (this.isFixedWeightProduct) {
                regionalPrice.rrp = data.unitPriceIncTax
              } else {
                regionalPrice.rrp = data.pricePerKgIncTax
              }
              regionalPrice.grossProfitPercentage = 0
            }
          })
          .catch(() => {
            regionalPrice.grossProfitPercentage = null
            regionalPrice.unitPriceExGst = null
            regionalPrice.unitPriceIncTax = null
            regionalPrice.pricePerKgIncTax = null
            regionalPrice.pricePerKgExGst = null
          })
          .finally(() => {
            this.calculatingGPP = false
            this.calculatingGPPRegionCodes.splice(
              calculationGPPRegionCodesIndex,
              1
            )
          })
      } else {
        regionalPrice.grossProfitPercentage = null
        regionalPrice.grossProfitPercentage = null
        regionalPrice.unitPriceExGst = null
        regionalPrice.unitPriceIncTax = null
        regionalPrice.pricePerKgIncTax = null
        regionalPrice.pricePerKgExGst = null
        this.calculatingGPP = false
        this.calculatingGPPRegionCodes.splice(calculationGPPRegionCodesIndex, 1)
      }
    },

    getRegionPrice(regionCode) {
      return this.product.regionalPrices?.find(
        ({ pricingRegionCode }) => pricingRegionCode === regionCode
      )
    },

    getRegionCartonPrice(regionCode) {
      return this.getRegionPrice(regionCode)?.price
    },

    getRegionUnitPrice(regionCode, includingTax = false) {
      // in: cartonPrice, rrpIncTax, units or cartonWeight, taxCodes
      // out: gpp, unit price inc tax, unit price ex tax

      if (!this.product.packageQuantity) return 0
      const price = this.getRegionPrice(regionCode)
      if (
        this.isUSCountry &&
        price?.price &&
        (price?.unitPriceExGst === null ||
          price?.unitPriceExGst === undefined ||
          Number.isNaN(Number(price?.unitPriceExGst)))
      ) {
        const packageQuantity = Number(this.product.packageQuantity) || 0
        if (packageQuantity <= 0) return 0

        const derivedUnitPrice = _.round(
          Number(price.price) / packageQuantity,
          2
        )
        return derivedUnitPrice
      }
      return (
        (includingTax ? price?.unitPriceIncTax : price?.unitPriceExGst) || 0
      )
    },

    getRegionCostPerKilo(regionCode, includingTax = false) {
      if (!this.product.variableWeightTargetInKg) return 0
      const price = this.getRegionPrice(regionCode)
      if (
        this.isUSCountry &&
        price?.price &&
        (price?.pricePerKgExGst === null ||
          price?.pricePerKgExGst === undefined ||
          Number.isNaN(Number(price?.pricePerKgExGst)))
      ) {
        const variableWeightTargetInKg =
          Number(this.product.variableWeightTargetInKg) || 0
        if (variableWeightTargetInKg <= 0) return 0

        const derivedCostPerKilo = _.round(
          Number(price.price) / variableWeightTargetInKg,
          2
        )
        return derivedCostPerKilo
      }
      return (
        (includingTax ? price?.pricePerKgIncTax : price?.pricePerKgExGst) || 0
      )
    },

    hasValidTargetStorePricingRegion(region) {
      if (!region?.price) return false
      if (this.isUSCountry) return true
      return !!region.rrp
    },

    getNormalizedRegionalPrices(
      regionalPrices = this.product.regionalPrices || []
    ) {
      const preserveExistingRegionalPrice = (regionalPrice = {}) => {
        const existingRegionalPrice =
          this.originalProduct?.regionalPrices?.find(
            ({ pricingRegionCode }) =>
              pricingRegionCode === regionalPrice.pricingRegionCode
          )

        if (!existingRegionalPrice || regionalPrice.id) {
          return regionalPrice
        }

        return {
          ...existingRegionalPrice,
          ...regionalPrice
        }
      }

      const preserveExistingRegionalPrices = (prices = regionalPrices) =>
        prices.map((regionalPrice) =>
          preserveExistingRegionalPrice(regionalPrice)
        )

      if (!this.newProduct) {
        return preserveExistingRegionalPrices()
      }

      if (this.isNationalPricing) {
        return this.pricingRegions.map((region) =>
          preserveExistingRegionalPrice({
            ...regionalPrices[0],
            pricingRegionCode: region.code
          })
        )
      }

      if (this.isStatePricing) {
        return this.pricingRegions
          .map((region) => {
            const state = regionalPrices.find(
              (state) => state.state === region.state
            )

            return state
              ? preserveExistingRegionalPrice({
                  ...state,
                  pricingRegionCode: region.code
                })
              : {}
          })
          .filter((region) => !_.isEmpty(region))
      }

      return preserveExistingRegionalPrices()
    },

    // ***
    // Related to saving / editing product
    // ***

    constructRangeRulePayload(
      product = this.product,
      metaData = this.metaData,
      regionalPrices = product.regionalPrices || []
    ) {
      return {
        targetBannerGroupIds: metaData.targetBannerGroupIds,
        targetMultiStoreOperatorIds: metaData.targetMultiStoreOperatorIds,
        targetStoreIds: metaData.targetStoreIds,
        targetStorePricingRegions: regionalPrices
          .filter((region) => this.hasValidTargetStorePricingRegion(region))
          .map((region) => region.pricingRegionCode)
      }
    },

    shouldSubmitDefaultGeographyRangeRule(regionalPrices) {
      if (
        this.newProduct ||
        !this.isProductStatusDraft ||
        this.metaData.targetType !== TARGET_TYPE.GEOGRAPHY
      )
        return false

      const originalRangeRule = this.originalProduct?.rangeRule || {}
      const hasPersistedTargetPricingRegions =
        originalRangeRule.targetStorePricingRegions?.length > 0

      return (
        !hasPersistedTargetPricingRegions &&
        regionalPrices.some((region) =>
          this.hasValidTargetStorePricingRegion(region)
        )
      )
    },

    constructAPIPayload() {
      // Only product changes are submitted via API
      const payload = this.productChanges
      const normalizedRegionalPrices = this.getNormalizedRegionalPrices()

      if (this.isGroceryTypeProduct) {
        delete payload.liquorInformation
      }

      if (this.isLiquorTypeProduct) {
        delete payload.dietaryInformation
      }

      const shouldSubmitDefaultGeographyRangeRule =
        typeof this.shouldSubmitDefaultGeographyRangeRule === 'function' &&
        this.shouldSubmitDefaultGeographyRangeRule(normalizedRegionalPrices)

      if (
        this.newProduct ||
        this.hasRangeRuleChanges ||
        shouldSubmitDefaultGeographyRangeRule
      ) {
        payload.rangeRule = this.constructRangeRulePayload(
          this.product,
          this.metaData,
          normalizedRegionalPrices
        )
      }

      if (
        payload.regionalPrices !== undefined &&
        this.hasBusinessSignificantRegionalPriceChanges()
      ) {
        payload.regionalPrices = normalizedRegionalPrices
      } else if (payload.regionalPrices !== undefined) {
        delete payload.regionalPrices
      }

      // If the GST or WET setting has changed then submit all the regional prices so that rrp ex tax values can be recalculated
      if (payload.hasGst !== undefined || payload.hasWet !== undefined) {
        Object.assign(payload, { regionalPrices: normalizedRegionalPrices })
      }

      if (this.newProduct || this.hasTaxSettingsChanges) {
        payload.hasGst = this.product.hasGst
        payload.hasWet = this.product.hasWet
      }

      if (
        this.isUSCountry &&
        this.product.usTaxCategoryId &&
        (this.newProduct || this.productChanges.usTaxCategoryId !== undefined)
      ) {
        payload.usTaxCategoryId = this.product.usTaxCategoryId
      }

      if (this.isVariableWeightProduct) {
        payload.unit = 'KG'
        payload.baseUnitGtin = payload.gtin
      }

      // If requires same Gtin, set Carton Gtin equal to Unit Gtin
      if (this.requiresSameGtin) payload.gtin = payload.baseUnitGtin

      if (!_.isEmpty(payload))
        payload.tradeItemDescription = this.tradeItemDescription

      const shouldIncludePublishDate =
        !!this.metaData.publishDate &&
        (this.newProduct ||
          this.isProductStatusDraft ||
          this.hasPublishDateChanges)

      // Only submit publish date when this save is actually meant to create/update it
      if (shouldIncludePublishDate)
        payload.publishAtDate = this.metaData.publishDate

      // Add images types to be deleted (if any)
      if (this.imageTypesToDelete.length)
        payload.imageTypesToDelete = this.imageTypesToDelete

      // Delete images changes from payload as these are not required by API
      if (payload.images) delete payload.images

      return payload
    },

    confirmSave() {
      const productPayload = this.constructAPIPayload()

      return this.$emit(
        'saveProduct',
        {
          productCode: this.product.productCode,
          productType: this.product.productType,
          soldAs: this.product.soldAs,
          ...productPayload
        },
        this.newImages
      )
    },

    handleSave() {
      if (
        // If live, in Family Group and with FG changes, ask user manual confirmation
        this.product.live &&
        this.isProductInFamilyGroup &&
        this.hasFamilyGroupRelatedChanges
      ) {
        this.familyGroupChangesDialog = true
      } else if (
        (this.newProduct || this.isProductStatusDraft) &&
        !this.metaData.publishDate
      ) {
        // If new or draft product and has no publish date set, show dialog
        this.publishDateDialog = true
      } else this.confirmSave()
    },

    // ***
    // Related to displaying product
    // ***

    setPanelValue() {
      if (this.newProduct) this.panel = null
      if (this.isRecentBulkUploadedProduct) this.panel = 1
    },

    getProductMetaData(product, newProduct) {
      // So that UI-required properties can be passed between components
      return {
        initialPublishDate: newProduct ? '' : product.publishDate,
        publishDate: newProduct ? '' : product.publishDate,
        targetGeography: newProduct
          ? TARGET_GEOGRAPHY.NATIONAL
          : TARGET_GEOGRAPHY.ZONE,
        targetType: newProduct
          ? TARGET_TYPE.GEOGRAPHY
          : this.getTargetType(product.rangeRule),
        targetBannerGroupIds: newProduct
          ? []
          : product?.rangeRule?.targetBannerGroups?.map(
              (bg) => bg.bannerGroupId
            ),
        targetMultiStoreOperatorIds: newProduct
          ? []
          : product?.rangeRule?.targetMultiStoreOperators?.map(
              (mso) => mso.multiStoreOperatorId
            ),
        targetStoreIds: newProduct
          ? []
          : product?.rangeRule?.targetStores?.map((store) => store.storeId)
      }
    },

    getProductValidators() {
      return {
        validProductDetails: null
      }
    },

    getTargetType(rangeRule) {
      if (!rangeRule?.id) return null

      if (rangeRule.targetBannerGroups?.length) return TARGET_TYPE.BANNER_GROUP
      else if (rangeRule.targetMultiStoreOperators?.length)
        return TARGET_TYPE.MULTI_STORE_OPERATOR
      else if (rangeRule.targetStores?.length) return TARGET_TYPE.RETAILER
      else return TARGET_TYPE.GEOGRAPHY
    },

    setDefaultAwardsRating(product) {
      product.liquorInformation.awards = {
        ...DEFAULT_AWARDS_DATA,
        ...product.liquorInformation.awards
      }
    },

    setNewProductDefaults(product) {
      product.productType = this.isILR
        ? PRODUCT_TYPES.LIQUOR.value
        : PRODUCT_TYPES.GROCERY.value
      product.soldAs = PRODUCT_SOLD_AS.FIXED_WEIGHT
    },

    setProductDefaults(product) {
      if (this.newProduct) this.setNewProductDefaults(product)
      this.setDefaultAwardsRating(product)
    }
  },

  async mounted() {
    this.setPanelValue()
    this.setProductDefaults(this.product)

    // Object with ui-required properties that can be passed down to components as props
    this.metaData = this.getProductMetaData(this.product, this.newProduct)
    // Object with product-required validators that can be passed down to components as props
    this.validators = this.getProductValidators(this.product)

    // Created so that changes in product are sent to backend when saving / editing product
    this.originalProduct = _.cloneDeep(this.product)

    // Created so that changes in product and ui elements can be detected
    this.initialBluePrint = _.cloneDeep(this.createBluePrint())
  },

  watch: {
    // When panels are expanded this gets triggered
    panel() {
      if (this.panel === undefined) return
      if (!this.$refs[`panel-${this.panel}`]) return

      setTimeout(() => {
        this.$refs[`panel-${this.panel}`].$el.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, PANELS_SCROLLING_DELAY)
    },

    // 'product.shortDescription'(desc) {
    //   this.product.shortDescription = _.startCase(_.toLower(desc))
    // },

    'product.hasWet'(hasWet) {
      if (hasWet) this.product.hasGst = true
    },

    'product.hasGst'(hasGst) {
      if (!hasGst) this.product.hasWet = false
    },

    'product.productType'(newType, oldType) {
      if (this.newProduct && oldType && newType !== oldType) {
        this.resetNewProductDraftForType(newType)
        return
      }

      if (newType === PRODUCT_TYPES.SERVICE.value) {
        this.product.soldAs = PRODUCT_SOLD_AS.FIXED_WEIGHT
        this.product.unitQty = 1
      }
    },

    'product.unit': {
      immediate: true,
      handler(newUnit) {
        if (!this.isServicesTypeProduct) return

        const u = (newUnit || '').toUpperCase()

        if (u === 'EA') {
          const prev = this.product.packageQuantity

          this.product.packageQuantity = 1

          if (this.product.regionalPrices?.length && Number(prev) !== 1) {
            this.$nextTick(() => this.debounceRecalcAll())
          }
        } else {
          if (this.product.packageQuantity === 1) {
            this.product.packageQuantity = ''
          }

          if (this.product.regionalPrices?.length) {
            this.$nextTick(() => this.debounceRecalcAll())
          }
        }
      }
    },

    'product.packageQuantity'(newVal, oldVal) {
      if (!this.isServicesTypeProduct) return

      const u = (this.product.unit || '').toUpperCase()
      if (u === 'EA' && Number(newVal) !== 1) {
        this.product.packageQuantity = 1
        this.$nextTick(() => this.debounceRecalcAll())
        return
      }

      if (u !== 'EA' && Number(newVal) !== Number(oldVal)) {
        if (this.product.regionalPrices?.length) {
          this.$nextTick(() => this.debounceRecalcAll())
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

#edit-product {
  .v-chip__content {
    margin: auto !important;
    font-size: 12px;
  }
}
</style>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#edit-product {
  display: flex !important;
  flex-direction: column;
  overflow: hidden;
  box-shadow: none;
  background: $bg-secondary-color;
  .v-card__subtitle,
  .v-card__text,
  .v-expansion-panel,
  .v-expansion-panel-text,
  .v-expansion-panel-title {
    background: white;
  }
  .v-card__text > .v-card,
  .v-expansion-panel {
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  }
  .v-expansion-panel-title {
    align-items: center;
    gap: 12px;
  }
  .p-content-div {
    flex-grow: 1;
    overflow: auto;
  }
  .p-chip {
    &-guide {
      max-width: auto;
      margin-right: 10px;
      letter-spacing: 0.25px;
      border: 1px solid;
    }
    &-guide-70px {
      max-width: 70px;
    }
    &-guide-90px {
      max-width: 90px;
    }
    &-ready {
      background-color: $green-color-bg;
      color: $green-color;
    }
    &-error {
      background-color: $red-color-bg;
      color: $red-color;
    }
    &-alert {
      background-color: $orange-color-bg;
      color: $orange-color;
    }
  }
  .p-product-description {
    font-size: 16px;
  }
  .v-sheet {
    position: relative;
  }
  label.image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
    cursor: pointer;
    &:hover,
    &.show {
      opacity: 1;
    }
    p {
      font-size: 18px;
      font-weight: bold;
      color: white;
    }
    .text-overlay {
      font-size: 12px !important;
      text-transform: uppercase;
      letter-spacing: 0.2px;
    }
  }
  td,
  th {
    border: 0;
    vertical-align: top;
    text-align: center;
  }
  th span {
    font-size: 12px;
    text-transform: uppercase;
  }
}

@media only screen and (min-width: 960px) {
  #edit-product {
    th span {
      font-size: 14px;
    }
    .p-product-description {
      font-size: 20px;
    }
    .text-overlay p {
      font-size: 10px;
    }
  }
}
</style>
